import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { format } from 'date-fns';

interface DashboardData {
  financial: {
    totalBilled: number;
    totalPaid: number;
    outstandingBalance: number;
    currency: string;
    paymentHistory: Array<{ date: string; amount: number }>;
  };
  hours: {
    thisMonth: number;
    total: number;
    unbilled: number;
    approved: number;
  };
  contractor: {
    name: string;
    hourlyRate: number;
    paymentTerms: string;
  };
  invoices?: {
    draft: number;
    approved: number;
    rejected: number;
    pending?: number;
    paid?: number;
    recent: Array<{
      id: string;
      invoiceNumber: string;
      status: string;
      total: number;
      startDate: string;
      endDate: string;
    }>;
  };
}

const ContractorDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contractor/dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return <Typography>No data available</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Welcome, {data.contractor.name}!
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Financial Summary Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Summary
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">Total Billed</Typography>
                  <Typography variant="h6">
                    {data.financial.currency} {data.financial.totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">Total Paid</Typography>
                  <Typography variant="h6" color="success.main">
                    {data.financial.currency} {data.financial.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">Outstanding Balance</Typography>
                  <Typography variant="h6" color={data.financial.outstandingBalance > 0 ? 'error' : 'success.main'}>
                    {data.financial.currency} {data.financial.outstandingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>
              </Box>
              {data.financial.paymentHistory.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Payment History
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={data.financial.paymentHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="amount" stroke="#4caf50" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Hours Summary Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hours Summary
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">This Month</Typography>
                  <Typography variant="h6">
                    {data.hours.thisMonth.toFixed(2)} hours
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">Total Hours</Typography>
                  <Typography variant="h6">
                    {data.hours.total.toFixed(2)} hours
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="textSecondary">Unbilled Hours</Typography>
                  <Typography variant="h6" color="warning.main">
                    {data.hours.unbilled.toFixed(2)} hours
                  </Typography>
                </Box>
                {data.hours.approved !== undefined && (
                  <Box display="flex" justifyContent="space-between" mb={2}>
                    <Typography color="textSecondary">Approved Hours (Ready to Bill)</Typography>
                    <Typography variant="h6" color="success.main">
                      {data.hours.approved.toFixed(2)} hours
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/contractor/time-entries/new')}
                >
                  Log New Hours
                </Button>
                {data.hours.approved !== undefined && data.hours.approved > 0 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ReceiptIcon />}
                    onClick={() => navigate('/contractor/invoices')}
                  >
                    Generate Bill ({data.hours.approved.toFixed(0)} approved hours)
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Status Card */}
        {data.invoices && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Invoice Status</Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/contractor/invoices')}
                  >
                    View All
                  </Button>
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box textAlign="center">
                      <Chip
                        label={`${data.invoices.draft} Draft`}
                        color="warning"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Awaiting Admin Approval
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box textAlign="center">
                      <Chip
                        label={`${data.invoices.approved} Approved`}
                        color="success"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Final Invoices
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box textAlign="center">
                      <Chip
                        label={`${data.invoices.pending || 0} Pending`}
                        color="warning"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Awaiting Payment
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box textAlign="center">
                      <Chip
                        label={`${data.invoices.paid || 0} Paid`}
                        color="success"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Payment Received
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Box textAlign="center">
                      <Chip
                        label={`${data.invoices.rejected} Rejected`}
                        color="error"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Needs Review
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                {data.invoices.recent && data.invoices.recent.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recent Invoices
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Invoice #</TableCell>
                            <TableCell>Period</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.invoices.recent.slice(0, 5).map((invoice) => (
                            <TableRow key={invoice.id}>
                              <TableCell>
                                <Link
                                  component="button"
                                  onClick={() => navigate(`/contractor/invoices/${invoice.id}`)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  {invoice.invoiceNumber}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {format(new Date(invoice.startDate), 'MMM yyyy')}
                              </TableCell>
                              <TableCell align="right">${invoice.total.toFixed(2)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={invoice.status}
                                  size="small"
                                  color={
                                    invoice.status === 'APPROVED' ? 'success' :
                                    invoice.status === 'DRAFT' ? 'warning' :
                                    invoice.status === 'PENDING' ? 'warning' :
                                    invoice.status === 'PAID' ? 'success' :
                                    invoice.status === 'REJECTED' ? 'error' : 'default'
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ContractorDashboard;


